import styles from './style.postcss';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classnames from 'classnames';
import { List } from 'immutable';
import is from 'is_js';

import { connect,
  PrivilegeChecker,
  Tooltip,
  Icon,
  PageCard,
  testClass } from 'omni-common-ui';
import Privileges from 'domain/Privileges';
import CourseViewTable from 'components/CourseViewTable';
import { actions as GroupMembers } from 'data/GroupMembers';
import { accessors as ketExamsAccessors,
  KET_EXAM_TYPE_MOCK,
  KET_EXAM_TYPE_REAL } from 'data/KetExams';

import { MOCK_EXAM_COUNT } from '../component';

const EMPTY_HYPHEN = '-';
const NO_MATCH_SLASH = '/';

const FINAL_SCORE_COLUMN = 'ket-final-score';
const MARKED_BY_COLUMN = 'ket-marked-by';
const MARK_SCORE_COLUMN = 'ket-mark-score';
const MOCK_COLUMN_PREFIX = 'ket-mock-';

export class KetExamListView extends Component {
  _getMarkRoute(studentId) {
    const { params: { groupId, mockIndex }, examType, buildRoute } = this.props;
    if (examType === KET_EXAM_TYPE_MOCK) {
      return buildRoute(`/group/${groupId}/ket-exam/${mockIndex || '1'}/student/${studentId}`);
    }
    return buildRoute(`/group/${groupId}/ket-score/student/${studentId}`);
  }

  _renderSkillCell(result, skill) {
    const skillResult = result && result.skillResults.get(skill.skillId);
    const mark = skillResult && skillResult.get('mark');
    const scale = skillResult && skillResult.get('scale');
    if (! is.number(mark)) {
      return <span>{EMPTY_HYPHEN}</span>;
    }
    if (! is.number(scale)) {
      return <Tooltip text={`Marks ${mark} = ${NO_MATCH_SLASH}`}
          placement="top"
          trigger={['hover']}>
        <span className={classnames(styles.KetExamListView_score, styles.__fail)}>
          {NO_MATCH_SLASH}
        </span>
      </Tooltip>;
    }

    const isFail = ketExamsAccessors.isFailBanding(skillResult.get('grade'));
    const content = <span className={classnames(styles.KetExamListView_score,
        { [styles.__fail]: isFail })}>
      {scale}
    </span>;
    return <Tooltip text={`Marks ${mark} = Cambridge English Scale ${scale}`}
        placement="top"
        trigger={['hover']}>
      {content}
    </Tooltip>;
  }

  _renderFinalScoreCell(result, exam) {
    if (! exam || ! exam.data) {
      return <span>{EMPTY_HYPHEN}</span>;
    }
    const skills = exam.data.skills;
    const allMarked = skills.every((skill) => {
      const skillResult = result && result.skillResults.get(skill.skillId);
      return skillResult && is.number(skillResult.get('mark'));
    });
    if (! allMarked) {
      return <span>{EMPTY_HYPHEN}</span>;
    }

    const scales = skills.map((skill) => {
      const skillResult = result.skillResults.get(skill.skillId);
      const scale = skillResult && skillResult.get('scale');
      return is.number(scale) ? scale : 0;
    }).toArray();
    const finalScore = ketExamsAccessors.computeOverallScore(scales);
    if (! is.number(finalScore)) {
      return <span>{EMPTY_HYPHEN}</span>;
    }

    const banding = ketExamsAccessors.mapScaleToBanding(
      exam.data.scaleBandings, finalScore);
    const isFail = banding && ketExamsAccessors.isFailBanding(banding.grade);
    const content = <span className={classnames(styles.KetExamListView_score,
        { [styles.__fail]: isFail })}>
      {finalScore}
    </span>;
    if (! banding) return content;
    const bandingText = ketExamsAccessors.formatGradeBanding(banding.grade, banding.level);
    return <Tooltip text={`Passtype: ${bandingText}`}
        placement="top"
        trigger={['hover']}>
      {content}
    </Tooltip>;
  }

  _renderMarkScoreCell(student) {
    const { examType } = this.props;
    const label = examType === KET_EXAM_TYPE_REAL ? 'Record Score' : 'Mark Score';
    return <Link to={this._getMarkRoute(student.studentGeneralInfo.id)}
        className={classnames(styles.KetExamListView_markLink, testClass('ket-mark-score-link'))}>
      <span>{label}</span>
      <Icon id="arrow-right" className={styles.KetExamListView_markLink_icon} />
    </Link>;
  }

  _renderMockExamCell(student, mockIndex) {
    const { mockExams } = this.props;
    const mockExam = (mockExams || []).find((item) => item.mockIndex === mockIndex);
    const mockExamData = mockExam && mockExam.exam;
    if (! mockExamData || ! mockExamData.data) {
      return <span>{EMPTY_HYPHEN}</span>;
    }
    const result = mockExamData.data.studentResults.get(`${student.studentGeneralInfo.id}`);
    return this._renderFinalScoreCell(result, mockExamData);
  }

  _renderCell(student, columnId) {
    const { exam } = this.props;
    const studentId = student.studentGeneralInfo.id;
    const result = exam.data.studentResults.get(`${studentId}`);

    if (typeof columnId === 'string' && columnId.indexOf(MOCK_COLUMN_PREFIX) === 0) {
      const mockIndex = Number.parseInt(columnId.slice(MOCK_COLUMN_PREFIX.length), 10);
      return this._renderMockExamCell(student, mockIndex);
    }

    switch (columnId) {
      case FINAL_SCORE_COLUMN:
        return this._renderFinalScoreCell(result, exam);
      case MARKED_BY_COLUMN:
        return <span>{(result && result.markedBy) || EMPTY_HYPHEN}</span>;
      case MARK_SCORE_COLUMN:
        return this._renderMarkScoreCell(student);
      default: {
        const skill = exam.data.skills.find(
          (examSkill) => examSkill.skillId === columnId);
        return this._renderSkillCell(result, skill);
      }
    }
  }

  render() {
    const { exam, members, canMark, examType } = this.props;
    if (! exam || ! exam.data || ! List.isList(members)) return null;

    const isReal = examType === KET_EXAM_TYPE_REAL;
    const finalScoreLabel = isReal ? 'KET Score' : 'Final Score';
    const markedByLabel = isReal ? 'Record by' : 'Marked by';

    const skills = exam.data.skills
      .sortBy((skill) => skill.orderingSequence);

    const leadingColumns = isReal ?
      Array.from({ length: MOCK_EXAM_COUNT }, (unused, offset) => offset + 1)
        .map((index) =>
          <CourseViewTable.Cell key={`${MOCK_COLUMN_PREFIX}${index}`}
              columnId={`${MOCK_COLUMN_PREFIX}${index}`}
              small>
            {`Mock Exam ${index}`}
          </CourseViewTable.Cell>) :
      skills.map((skill) =>
        <CourseViewTable.Cell key={skill.skillId}
            columnId={skill.skillId}
            small>
          {skill.name}
        </CourseViewTable.Cell>);

    return <div className={styles.KetExamListView}>
      <PageCard>
        <PageCard.Heading text={exam.data.name || 'Students'}
            className={styles.KetExamListView_cardHeader} />
        <PageCard.Content className={styles.KetExamListView_content}>
          <CourseViewTable students={members} onlyRowDivider>
            <CourseViewTable.Header>
              <CourseViewTable.Row>
                {leadingColumns}
                <CourseViewTable.Cell columnId={FINAL_SCORE_COLUMN} small>
                  {finalScoreLabel}
                </CourseViewTable.Cell>
                <CourseViewTable.Cell columnId={MARKED_BY_COLUMN} small>
                  {markedByLabel}
                </CourseViewTable.Cell>
                {canMark &&
                  <CourseViewTable.Cell columnId={MARK_SCORE_COLUMN} small>
                    <span className={testClass('ket-mark-score-header')} />
                  </CourseViewTable.Cell>}
              </CourseViewTable.Row>
            </CourseViewTable.Header>
            <CourseViewTable.Body>
              {(student, columnId) => this._renderCell(student, columnId)}
            </CourseViewTable.Body>
          </CourseViewTable>
        </PageCard.Content>
      </PageCard>
    </div>;
  }
}

KetExamListView.propTypes = {
  params: PropTypes.object.isRequired,
  examType: PropTypes.string,
  exam: PropTypes.object,
  mockExams: PropTypes.array,
  members: PropTypes.object,
  canMark: PropTypes.bool,
  buildRoute: PropTypes.func.isRequired,
};

export function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  return {
    members: GroupMembers.getMembersByGroup(state, groupId),
    canMark: PrivilegeChecker.hasPrivilege(state, Privileges.ProgressTest.Edit),
  };
}

export default connect(mapStateToProps)(KetExamListView);



// WEBPACK FOOTER //
// ./containers/Group/KetExam/ListView/component.jsx